import {
  HedgePlanBudgetTriggerModel,
  HedgePlanExecutionType,
  HedgePlanTimeTriggerModel,
  HedgePlanTradeModel,
  HedgePlanValueTriggerModel,
} from '../../../../Generated/Commodity-Management-Api';

// What a trade records about the trigger it fills; only the field for its execution type is set.
export type TradeTrigger = Pick<HedgePlanTradeModel, 'triggerDecilePercentile' | 'triggerPrice' | 'triggerDate'>;

export interface SeasonTriggers {
  value: HedgePlanValueTriggerModel[];
  budget: HedgePlanBudgetTriggerModel[];
  time: HedgePlanTimeTriggerModel[];
}

const MISSING_TRIGGER_SUFFIX = ' (no longer in this plan)';

function formatPrice(price: number): string {
  return price.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

// The dropdown label for a trigger; labels are unique within a season because each kind's value is.
export function toTriggerLabel(type: HedgePlanExecutionType, trigger: TradeTrigger): string | null {
  switch (type) {
    case HedgePlanExecutionType.Value:
      return trigger.triggerDecilePercentile != null ? `${trigger.triggerDecilePercentile}th decile` : null;
    case HedgePlanExecutionType.Budget:
      return trigger.triggerPrice != null ? formatPrice(trigger.triggerPrice) : null;
    case HedgePlanExecutionType.Time:
      return trigger.triggerDate ? trigger.triggerDate.substring(0, 10) : null;
    default:
      return null;
  }
}

export function fromTriggerLabel(type: HedgePlanExecutionType, label: string | null | undefined): TradeTrigger {
  const text = label?.replace(MISSING_TRIGGER_SUFFIX, '').trim();
  if (!text) {
    return { triggerDecilePercentile: null, triggerPrice: null, triggerDate: null };
  }

  return {
    triggerDecilePercentile: type === HedgePlanExecutionType.Value ? parseInt(text, 10) : null,
    triggerPrice: type === HedgePlanExecutionType.Budget ? Number(text.replace(/,/g, '')) : null,
    triggerDate: type === HedgePlanExecutionType.Time ? text : null,
  };
}

export function getTriggerLabels(type: HedgePlanExecutionType, triggers: SeasonTriggers): string[] {
  const seasonTriggers: TradeTrigger[] = type === HedgePlanExecutionType.Value
    ? triggers.value.map((trigger) => ({ triggerDecilePercentile: trigger.decilePercentile }))
    : type === HedgePlanExecutionType.Budget
      ? triggers.budget.map((trigger) => ({ triggerPrice: trigger.price }))
      : triggers.time.map((trigger) => ({ triggerDate: trigger.triggerDate }));

  return seasonTriggers.map((trigger) => toTriggerLabel(type, trigger)).filter((label): label is string => !!label);
}

// The label for the trigger a trade names, marked when the season no longer has that trigger.
export function getTradeTriggerLabel(type: HedgePlanExecutionType, trigger: TradeTrigger, triggers: SeasonTriggers): string | null {
  const label = toTriggerLabel(type, trigger);
  if (!label) {
    return null;
  }

  return getTriggerLabels(type, triggers).includes(label) ? label : `${label}${MISSING_TRIGGER_SUFFIX}`;
}

export function isMissingTriggerLabel(label: string | null | undefined): boolean {
  return !!label && label.endsWith(MISSING_TRIGGER_SUFFIX);
}
